import React from 'react';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import moment from 'moment';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { getBreakdownData } from '../ThroughputActions';
import { saveConfig, updateBreakdown, updateBreakdownType } from '../../../components/Filter/FilterActions';

import barIcon from '../../../assets/img/bar-icon.svg';
import pieIcon from '../../../assets/img/pie-icon.svg';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import { escapeHTML } from '../../../util/text';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';
import chartUtils from '../../../util/chartUtils';
import { WALLBOARD_TEXT_SIZE } from '../../../util/wallboardFrame';
import legendIcon from '../../../assets/img/legend.svg';
import { ViewSwitch, CardList, PieLegend, lightSlice, switchSlice, toggledNames } from '../../../components/CardViews/CardViews';
const pieConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    spacing: [0, 0, 0, 0],
    events: {
      render: function() {
        chartUtils.drawSliceEdges(this);
      }
    }
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false,
  },
  legend: {
    enabled: false,
    width: 230,
    layout: 'vertical',        
    align: 'left',            
    verticalAlign: 'top',
    itemMarginTop: 4,    
    itemMarginBottom: 4,
    itemStyle: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: 'rgba(0, 0, 0, 0.87)',
    }
  },
  tooltip: {
    useHTML: true,
    outside: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'category'
  },
  plotOptions: {
    pie: {
      center: ['74%', '50%'],
      ...chartUtils.DONUT_OPTIONS,
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: false,
        useHTML: true,
        formatter: function () {},
        style: {
          fontWeight: 'normal',
          fontSize: '12px',
        }
      }
    }
  },
  series: []
};

const BAR_SIDE = 19;

const barConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'bar',
    marginLeft: BAR_SIDE,
    marginRight: BAR_SIDE,
    events: {
      render: function() {
        chartUtils.roundTops(this);
        this.series.forEach(series => series.legendSymbol && series.legendSymbol.attr({ stroke: series.options.ring, 'stroke-width': 1 }));
      }
    }
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    itemMarginTop: 2,
    itemMarginBottom: 2,
    itemStyle: {
      fontWeight: 'normal'
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  // one bar, so no category beside it (it showed its index, "0")
  xAxis: {
    lineColor: chartUtils.TREND_AXIS_LINE_COLOR,
    tickLength: 0,
    labels: {
      enabled: false
    }
  },
  yAxis: {
    title: {
      text: ''
    },
    gridLineColor: chartUtils.TREND_GRID_COLOR,
    labels: {
      format: '{value}%',
      style: {
        color: chartUtils.TREND_LABEL_COLOR
      }
    },
    reversedStacks: false
  },
  plotOptions: {
    bar: {
      allowPointSelect: false,
      cursor: 'pointer',
      borderWidth: 0,
      states: chartUtils.BAR_STATES,
      dataLabels: {
        enabled: true
      }
    },
    series: {
      stacking: 'percent'
    }
  },
  series: []
};

const DEFAULT_POS = { x: 95, y: 85 };

export class ThroughputBreakdown extends React.Component {
  static defaultProps = {
    data: []
  };

  constructor(props) {
    super(props);

    this.state = {
      isPieChart: true,
      isFullSreen: false,
      isLabelsVisible: !!props.wallmode,
      isListView: !props.wallmode,
      hoveredSlice: null,
      hiddenSlices: [],
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
    this.chartContainer = React.createRef();
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    barConfig.exporting = { ...barConfig.exporting, ...this.props.chartExporting };
    pieConfig.exporting = { ...pieConfig.exporting, ...this.props.chartExporting };

    this.initChart();
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.updateFontSize();

    if (this.props.data.length) {
      this.props.getBreakdownData(this.props.data);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.props.getBreakdownData(this.props.data);
    }

    if (prevProps.breakdownData != this.props.breakdownData) {
      this.updateChartData();
      if (this.props.wallmode) {
        this.updateFontSize();
      }
    }

    if (prevProps.breakdownType != this.props.breakdownType) {
      this.updateChartType();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChartType() {
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.chart.destroy();

    this.initChart();
    this.updateFontSize();
    this.updateChartData();
  }

  initChart() {
    const config = this.props.breakdownType === 'pie' ? pieConfig : barConfig;
    const that = this;
    config.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
    if (this.props.breakdownType === 'pie') {
      config.plotOptions.pie.point = {
        events: {
          mouseOver() {
            chartUtils.liftSlice(this, true);
            that.setState({ hoveredSlice: this.name });
          },
          mouseOut() {
            chartUtils.liftSlice(this, false);
            that.setState({ hoveredSlice: null });
          }
        }
      };
    }
    config.plotOptions[this.props.breakdownType].dataLabels.formatter =
      this.props.breakdownType === 'pie'
        ? function() {
            return that.tooltipFormatter(that, this);
          }
        : function() {
            return that.dataLabel(that, this);
          };

    this.chart = new Highcharts['Chart'](this.chartContainer.current, config);
  }

  updateChartData() {
    if (this.state.hiddenSlices.length) {
      this.setState({ hiddenSlices: [] });
    }
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    const sorted = [...this.props.breakdownData];
    if (this.state.isPieChart) {
      this.chart.addSeries(
        {
          legendType: 'point',
          showInLegend: true,
          colorByPoint: true,
          data: chartUtils.donutData(sorted)
        },
        true
      );
    } else {
      const parts = chartUtils.stateSeries(sorted, 'borderColor');
      for (let i = 0; i < sorted.length; i++) {
        const data = {
          ...parts[i],
          data: [sorted[i].y]
        };
        this.chart.addSeries({ ...data, borderWidth: 1, ring: chartUtils.stateEdge(sorted[i].color) }, false);
      }
      this.chart.redraw();
    }
  }

  updateFontSize = () => {
    if (!this.chart) return;

    const isFullscreen = this.state.isFullSreen;
    const isLabelsVisible = this.state.isLabelsVisible;
    const pieSpacing = isFullscreen ? [10, 10, 15, 10] : [0, 0, 0, 0];
    const that = this;

    const exportPieLabels = this.state.isPieChart ? chartUtils.pieExportLabels(point => `${point.y} ${point.y === 1 ? 'item' : 'items'}`) : null;
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Throughput breakdown',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },
          ...(exportPieLabels ? {} : {
            legend: {
              itemStyle: {
                color: 'rgba(0, 0, 0, 0.87)',
                fontSize: '12px',
                fontFamily: 'Poppins'
              }
            }
          }),
          plotOptions: {
            ...(exportPieLabels ? { pie: { dataLabels: exportPieLabels } } : {}),
            bar: {
              dataLabels: {
                style: {
                  textOutline: 'none'
                }
              }
            }
          }
        }
      },
      chart: {
        // State lags the rendered chart type on the first render
        spacing: this.props.breakdownType === 'pie' ? pieSpacing : [10, 0, 15, 0]
      },
      legend: {
        enabled: this.state.isPieChart && !isFullscreen ? false : isLabelsVisible,
        itemMarginTop: 4,
        ...(this.state.isPieChart || isFullscreen ? chartUtils.pieLegend(isFullscreen) : chartUtils.cardLegend(false))
      },
      plotOptions: {
        bar: {
          dataLabels: chartUtils.segmentLabelStyle(this.props.wallmode ? WALLBOARD_TEXT_SIZE : (this.state.isFullSreen ? '13px' : '12px'))
        },
        pie: {
          center: isFullscreen ? ['50%', '50%'] : chartUtils.cardPieCenter(this.chart, this.props.wallmode),
          size: isFullscreen ? '60%' : '90%',
          dataLabels: isFullscreen ? {
            enabled: true,
            useHTML: true,
            connectorWidth: 2,
            //alignTo: 'plotEdges',
            distance: 20,
            crop: false,
            overflow: 'justify',
            style: {
              fontSize: '16px',
              fontWeight: 'normal',
              textOutline: 'none',
              whiteSpace: 'normal',
            },
            formatter: function () {
              const name = escapeHTML(this.key);
              const number = this.y;
              const percentage = this.percentage.toFixed(2);
              return `<div class="tooltip-label-item tooltip-label-box" style="${chartUtils.pieLabelStyle(this, 20)}"><strong>${name} (${percentage}%) </strong>${number} ${number === 1 ? 'item' : 'items'}</div>`;
            }
          } : {
            enabled: false,
            style: {
              fontSize: '11px'
            }
          }
        }        
      }
    });
    chartUtils.placePieNames(this, this.state.isPieChart && !this.state.isFullSreen);
    this.chart.reflow();
  };

  handleSwitch = () => {
    this.setState({ isPieChart: !this.state.isPieChart }, () => {
      this.props.setChartType(this.state.isPieChart ? 'pie' : 'bar');
      this.props.saveConfig();
    });
  };
  
  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          isFullSreen: false,
          isLabelsVisible: this.labelsBeforeFocus,
          pos: DEFAULT_POS
        },
        () => this.updateChartType()
      );
    } else {
      this.labelsBeforeFocus = this.state.isLabelsVisible;
      this.setState(
        {
          width: '100%',
          height: '100%',
          isFullSreen: true,
          isLabelsVisible: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateChartType()
      );
    }
  };

  getView() {
    if (this.state.isListView) {
      return 'list';
    }
    return this.state.isPieChart ? 'pie' : 'bar';
  }

  handleView = view => {
    if (view === 'list') {
      this.setState({ isListView: true });
      return;
    }
    this.setState({ isListView: false });
    if ((view === 'pie') !== this.state.isPieChart) {
      this.handleSwitch();
    }
  };

  renderRankedList() {
    const data = this.props.breakdownData || [];
    const rows = chartUtils.rankedShares(data).map(item => ({
      name: item.name,
      color: item.color,
      values: [item.share, `${item.y} ${item.y === 1 ? 'item' : 'items'}`]
    }));
    return <CardList keyDots columns={['Share', 'Items']} rows={rows} />;
  }

  highlightSlice = (name, on) => {
    if (lightSlice(this.chart, name, on) || !on) {
      this.setState({ hoveredSlice: on ? name : null });
    }
  };

  toggleSlice = name => {
    switchSlice(this.chart, name);
    this.setState({ hiddenSlices: toggledNames(this.state.hiddenSlices, name) });
  };

  tooltipFormatter(comp, ev) {
    const name = comp.props.breakdownType === 'pie' ? ev.key : ev.series.name;
    return `<div class="tooltip-label-item tooltip-label-box" style="color: #001A33"><span class="label-box" style="background-color: ${ev.color}";></span><strong>${name}: </strong> (${ev.y} ${ev.y === 1 ? 'item' : 'items'}) ${ev.percentage.toFixed(2)}%</div>`;
  }

  dataLabel(comp, ev) {
    return chartUtils.segmentLabel(ev);
  }

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box breakdown ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className='box_white_title'>
            <h3>Throughput breakdown</h3>
            <div className='right-actions'>
              {/* the card's three views in one control, like the Breakdown card's: the list, the pie and the bars */}
              <ViewSwitch
                views={[{ id: 'list', icon: legendIcon }, { id: 'pie', icon: pieIcon }, { id: 'bar', icon: barIcon }]}
                active={this.getView()}
                onChange={this.handleView}
              />
              <Button variant='secondary' size='sm'
                className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullSreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          <div ref={this.chartContainer} className='no-stroke' />
          {this.state.isListView && this.renderRankedList()}
          {!this.state.isListView && this.state.isPieChart && !this.state.isFullSreen && (
            <PieLegend
              offset={this.state.pieNamesOffset}
              data={this.props.breakdownData || []}
              hovered={this.state.hoveredSlice}
              hidden={this.state.hiddenSlices}
              onHover={this.highlightSlice}
              onToggle={this.toggleSlice}
            />
          )}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getBreakdownData: data => dispatch(getBreakdownData(data)),
    close: () => dispatch(updateBreakdown(false, 'trc')),
    setChartType: type => dispatch(updateBreakdownType(type, 'trc')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    wallmode: state.app.wallmode,
    breakdownData: state.throughput.breakdown,
    breakdownType: state.filters.trc.breakdownType
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ThroughputBreakdown));
